/** @format */

import {
	ChangeDetectorRef,
	Component,
	computed,
	DestroyRef,
	inject,
	input,
	OnInit
} from '@angular/core';
import {
	FulfillmentButtonInterface,
	selectInStorePickupButtonText,
	selectShipMode,
	setFulfillmentButtonsShipMode
} from '@pdp/add-to-cart/data-access';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
	selectAllProductSkusPartNumber,
	selectBundleInStorePickupButtonText,
	selectBundleProductsDataPartNumbers,
	selectIsBOPISLOnly,
	selectResolvedSkuPartNumber,
	selectSelectedBundleSkusPartNumbers
} from '@pdp/data-access/state';

import { Store } from '@ngrx/store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PdpFulfillmentButtonComponent } from '../../ui/fulfillment-button/fulfillment-button.component';
import {
	homefieldIconFulfillmentPickupFilled,
	homefieldIconFulfillmentPickupOutlined,
	HomefieldIconLibraryModule,
	HomefieldIconsLibraryService
} from '@dsg-tech/homefield-angular-icons';
import { ChangeStoreButtonComponent } from '../../change-store-button/change-store-button.component';
import { AsyncPipe, NgClass } from '@angular/common';

@Component({
	selector: 'pdp-feature-in-store-pickup-button',
	templateUrl: './in-store-pickup-button.component.html',
	styleUrl: './in-store-pickup-button.component.scss',
	imports: [
		PdpFulfillmentButtonComponent,
		HomefieldIconLibraryModule,
		ChangeStoreButtonComponent,
		NgClass,
		AsyncPipe
	]
})
export class InStorePickupButtonComponent implements OnInit {
	private readonly cdr = inject(ChangeDetectorRef);
	private readonly store = inject(Store);
	private readonly destroyRef = inject(DestroyRef);
	private readonly homefieldIconsLibraryService = inject(HomefieldIconsLibraryService);

	readonly isBundle = input(false);
	readonly nearbyStoresButtonEnabled = input(false);

	inStorePickupButtonText$: Observable<FulfillmentButtonInterface>;
	shipMode$ = this.store.select(selectShipMode);
	isBOPISLOnly$ = this.store.select(selectIsBOPISLOnly);

	productSkusPartNumber = computed(() =>
		this.isBundle()
			? this.store.selectSignal(selectBundleProductsDataPartNumbers)()
			: this.store.selectSignal(selectAllProductSkusPartNumber)()
	);

	resolvedSkuId = computed(() =>
		this.isBundle()
			? this.store.selectSignal(selectSelectedBundleSkusPartNumbers)()
			: this.store.selectSignal(selectResolvedSkuPartNumber)()
	);

	fulfillmentIconName = 'fulfillment-pickup-outlined';

	constructor() {
		this.homefieldIconsLibraryService.registerIcons([
			homefieldIconFulfillmentPickupFilled,
			homefieldIconFulfillmentPickupOutlined
		]);
	}

	ngOnInit(): void {
		this.inStorePickupButtonText$ = this.getInStorePickupButtonTextSelector();
		this.addFeatureFlagToButtonTextObservable(this.inStorePickupButtonText$);
		combineLatest([this.inStorePickupButtonText$, this.shipMode$, this.isBOPISLOnly$])
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(([inStorePickupButtonText, shipMode, isBOPISLOnly]) => {
				if (isBOPISLOnly) {
					this.setShipMode(inStorePickupButtonText.isBopl);
				} else if (shipMode === 'bopis' && inStorePickupButtonText.isBopl) {
					this.setShipMode(true);
				} else if (shipMode === 'bopl' && !inStorePickupButtonText.isBopl) {
					this.setShipMode(false);
				}

				const buttonEnabled = !inStorePickupButtonText.fulfillmentButtonDisabledClass;
				this.getFulfillmentIconName(shipMode, buttonEnabled);
				this.cdr.detectChanges();
			});
	}

	addFeatureFlagToButtonTextObservable(buttonText$: Observable<FulfillmentButtonInterface>) {
		this.inStorePickupButtonText$ = buttonText$.pipe(
			map((buttonText: FulfillmentButtonInterface) => {
				if (
					this.nearbyStoresButtonEnabled() &&
					buttonText.changeStoreButtonText?.includes('See')
				) {
					return {
						...buttonText,
						changeStoreButtonText: 'Available Nearby'
					};
				}
				return buttonText;
			})
		);
	}

	setShipMode(isBopl: boolean) {
		if (isBopl) {
			this.store.dispatch(setFulfillmentButtonsShipMode({ shipMode: 'bopl' }));
		} else {
			this.store.dispatch(setFulfillmentButtonsShipMode({ shipMode: 'bopis' }));
		}
	}

	getFulfillmentIconName(shipMode: string, buttonEnabled: boolean): void {
		const isBopisOrBopl = shipMode === 'bopis' || shipMode === 'bopl';
		if (isBopisOrBopl && buttonEnabled) {
			this.fulfillmentIconName = 'fulfillment-pickup-filled';
		} else {
			this.fulfillmentIconName = 'fulfillment-pickup-outlined';
		}
	}

	getInStorePickupButtonTextSelector(): Observable<FulfillmentButtonInterface> {
		return this.isBundle()
			? this.store.select(selectBundleInStorePickupButtonText)
			: this.store.select(selectInStorePickupButtonText);
	}
}
